import { useState, useEffect, useCallback } from "react";
import { LocationTypeOption } from "../constants";

interface UseLocationSelectorProps {
  initialLocationType: LocationTypeOption;
  initialLocationValues: string[];
  initialMatchType?: string;
  onLocationTypeChange: (locationType: LocationTypeOption) => void;
  onLocationValuesChange: (values: string[]) => void;
  onMatchTypeChange?: (matchType: string) => void;
}

export const useLocationSelector = ({
  initialLocationType,
  initialLocationValues,
  initialMatchType = "ANY",
  onLocationTypeChange,
  onLocationValuesChange,
  onMatchTypeChange,
}: UseLocationSelectorProps) => {
  const [locationType, setLocationType] =
    useState<LocationTypeOption>(initialLocationType);
  const [locationValues, setLocationValues] = useState<string[]>(
    initialLocationValues
  );
  const [matchType, setMatchType] = useState<string>(initialMatchType);

  useEffect(() => {
    setLocationType(initialLocationType);
  }, [initialLocationType]);

  useEffect(() => {
    // Update internal state if the initial prop changes
    setLocationValues(initialLocationValues);
  }, [initialLocationValues]);

  useEffect(() => {
    setMatchType(initialMatchType);
  }, [initialMatchType]);

  const handleLocationTypeChange = useCallback(
    (newType: LocationTypeOption) => {
      setLocationType(newType);
      onLocationTypeChange(newType);
    },
    [onLocationTypeChange]
  );

  const handleLocationValueChange = useCallback(
    (newValues: string[]) => {
      setLocationValues(newValues);
      onLocationValuesChange(newValues);
    },
    [onLocationValuesChange]
  );

  const handleMatchTypeChange = useCallback(
    (newValue: string) => {
      setMatchType(newValue);
      if (onMatchTypeChange) {
        onMatchTypeChange(newValue);
      }
    },
    [onMatchTypeChange]
  );

  return {
    locationType,
    locationValues,
    matchType,
    handleLocationTypeChange,
    handleLocationValueChange,
    handleMatchTypeChange,
  };
};
